import React, { useMemo, useRef } from "react";
import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  createHttpLink,
  from,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { setContext } from "@apollo/client/link/context";
import Cookies from "js-cookie";
import { useAppBridge } from "./Components/Shopify/ShopifyAppBridgeProvider";
import { getMarketplaceToken } from "./utils/marketplaceToken";

interface ApolloClientProviderProps {
  children: React.ReactNode;
}

const ApolloClientProvider: React.FC<ApolloClientProviderProps> = ({
  children,
}) => {
  const { isEmbedded, getSessionToken } = useAppBridge();

  // Use refs to always get fresh values in closures
  const isEmbeddedRef = useRef(isEmbedded);
  const getSessionTokenRef = useRef(getSessionToken);

  // Keep refs updated
  isEmbeddedRef.current = isEmbedded;
  getSessionTokenRef.current = getSessionToken;

  // GraphQL endpoint
  const httpLink = useMemo(
    () =>
      createHttpLink({
        uri: `${process.env.REACT_APP_BACKEND_URL}/graphql`,
      }),
    []
  );

  // Auth link: prefer the Shopify session token in embedded contexts; fall
  // back to the marketplace cookie JWT when running on mysharestock.org.
  // Both go in the same Authorization: Bearer header — the backend tries
  // session-token verification first and the marketplace JWT second.
  //
  // X-Shop-Domain is sent only for marketplace (non-embedded) requests,
  // where it tells the backend which connected shop the buyer is sourcing
  // from. Embedded requests derive shop_domain from the session token.
  const authLink = useMemo(
    () =>
      setContext(async (_, { headers }) => {
        // Detect embedded context (iframe inside Shopify Admin). When
        // embedded, App Bridge may still be initializing on the very first
        // render; poll briefly for window.shopify.idToken so we don't ship
        // a request without an auth header.
        let inIframe = false;
        try { inIframe = window.self !== window.top; } catch { inIframe = true; }

        if (inIframe) {
          const deadline = Date.now() + 3000;
          while (!window.shopify?.idToken && Date.now() < deadline) {
            await new Promise((r) => setTimeout(r, 50));
          }
        }

        let sessionToken: string | null = null;
        if (window.shopify?.idToken) {
          try {
            sessionToken = await getSessionTokenRef.current();
          } catch {
            // Fall through to cookie JWT.
          }
        }

        if (sessionToken) {
          // Embedded request. Always send the Shopify session token in
          // Authorization. If the merchant is also signed into ShareStock,
          // forward their marketplace JWT in X-Marketplace-Token so the
          // backend can identify their company in the same request — this
          // is what allows linkShopToCompany to verify both shop ownership
          // (session token dest claim) AND company ownership (marketplace
          // JWT) without a second round-trip.
          //
          // Prefer the in-memory token over the cookie: incognito/Safari
          // block third-party cookies inside the embedded iframe, so the
          // cookie is often silently dropped at Set time.
          const marketplaceJwt =
            getMarketplaceToken() || Cookies.get("authToken") || null;
          return {
            headers: {
              ...headers,
              authorization: `Bearer ${sessionToken}`,
              ...(marketplaceJwt
                ? { "X-Marketplace-Token": marketplaceJwt }
                : {}),
            },
          };
        }

        const cookieJwt = Cookies.get("authToken") || null;
        const shopFromCookie = Cookies.get("shopDomain");
        const shopFromStorage = localStorage.getItem("shopDomain");
        let shopDomain = shopFromCookie || shopFromStorage || null;
        if (shopDomain && !shopDomain.includes(".myshopify.com")) {
          shopDomain = `${shopDomain}.myshopify.com`;
        }

        return {
          headers: {
            ...headers,
            ...(cookieJwt ? { authorization: `Bearer ${cookieJwt}` } : {}),
            ...(shopDomain ? { "X-Shop-Domain": shopDomain } : {}),
          },
        };
      }),
    []
  );

  // Error handling with embedded app awareness
  const errorLink = useMemo(
    () =>
      onError(({ graphQLErrors, networkError }) => {
        if (graphQLErrors) {
          for (let err of graphQLErrors) {
            switch (err.extensions?.code) {
              case "UNAUTHENTICATED":
                // In embedded context, don't redirect to login - the session token should handle auth
                if (isEmbeddedRef.current) {
                  console.warn("Authentication failed in embedded context. Session token may have expired.");
                  // Could trigger a token refresh here if needed
                  // For now, just log the error - the next request will get a fresh token
                } else {
                  // Not embedded - redirect to login
                  Cookies.remove("authToken", {
                    secure: true,
                    sameSite: "Strict",
                  });
                  window.location.href = "/login";
                }
                break;
              default:
                console.error("[GraphQL Error]", err.message);
            }
          }
        }
        if (networkError) {
          console.error("[Network Error]:", networkError);
        }
      }),
    [] // Empty deps since we use refs
  );

  // Apollo client setup - recreate when isReady changes to pick up new auth context
  const client = useMemo(
    () =>
      new ApolloClient({
        link: from([errorLink, authLink.concat(httpLink)]),
        cache: new InMemoryCache(),
      }),
    [errorLink, authLink, httpLink]
  );

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export default ApolloClientProvider;
